import { IS_IOS_SAFARI } from '@/lib/common-react/utils/environment';
import { css } from '@emotion/react';
import useTranslation from 'next-translate/useTranslation';
import { ListArrowNextLightIcon } from 'public/assets';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface StoryGameAiImageCropPopupProps {
  initialImage: File;
  onCrop: (croppedImage: File) => void;
  onBack: () => void;
}

const StoryGameAiImageCropPopup: React.FC<StoryGameAiImageCropPopupProps> = ({
  initialImage,

  onCrop,
  onBack,
}) => {
  const { t } = useTranslation();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (initialImage) {
      setImageFile(initialImage);
      const url = URL.createObjectURL(initialImage);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [initialImage]);

  useEffect(() => {
    return () => {
      // Cleanup function to revoke the Blob URL when component unmounts
      if (croppedImageUrl) {
        URL.revokeObjectURL(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          1,
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(crop);
    },
    [],
  );

  const cropImage = useCallback(async () => {
    if (completedCrop && imgRef && imageFile && canvasRef.current) {
      const canvas = canvasRef.current;
      const scaleX = imgRef.naturalWidth / imgRef.width;
      const scaleY = imgRef.naturalHeight / imgRef.height;
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(
          imgRef,
          completedCrop.x * scaleX,
          completedCrop.y * scaleY,
          completedCrop.width * scaleX,
          completedCrop.height * scaleY,
          0,
          0,
          completedCrop.width,
          completedCrop.height,
        );

        canvas.toBlob((blob) => {
          if (blob) {
            const croppedFile = new File([blob], imageFile.name, {
              type: 'image/png',
            });

            // Create a Blob URL for download
            const blobUrl = URL.createObjectURL(blob);
            setCroppedImageUrl(blobUrl);
            onCrop(croppedFile);
          }
        }, 'image/png');
      }
    }
  }, [completedCrop, imgRef, imageFile, onCrop]);

  return (
    <div css={styles}>
      <div className="crop-container">
        <div className="crop-header">
          <div className="left">
            <button className="close-button" onClick={onBack}>
              <ListArrowNextLightIcon
                fill="#FFFFFF80"
                className="back-button"
              />
            </button>
            <div>{t('storygame_ai_onboarding_popup_title_crop_image')}</div>
          </div>
          {/* <button className="close-button" onClick={onCancel}>
            <CloseIconStoryGame />
          </button> */}
        </div>
        <div className="image-preview">
          {imageUrl && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
            >
              <img
                ref={(ref) => setImgRef(ref)}
                src={imageUrl}
                alt="Preview"
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>
        <div className="button-container">
          <button className="crop-button" onClick={cropImage}>
            자르기
          </button>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

const styles = css`
  @media (max-width: 1024px) {
    height: 100%;
    width: 100vw;
    ${IS_IOS_SAFARI ? 'max-height: -webkit-fill-available' : ''};
  }

  .crop-container {
    width: 420px;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 10px;
    padding: 20px;
    color: white;
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;

    .crop-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      width: 100%;

      .left {
        display: flex;
        align-items: center;
      }
    }
    .close-button {
      cursor: pointer;
    }
    @media (max-width: 1024px) {
      height: 100%;
      width: 100vw;
    }
  }

  .back-button {
    transform: rotate(180deg);
  }

  .image-preview {
    overflow: hidden;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  .image-preview img {
    object-fit: contain;
  }

  .button-container {
    display: flex;
    width: 100%;
    margin-top: 20px;
    justify-content: center;
  }

  .cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
  }

  .cancel-button {
    background: #333;
    color: white;
    flex: 1;
    margin-right: 10px;
  }

  .crop-button {
    background: #8b5cf6;
    color: white;
    margin-left: 10px;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    align-self: center;
    width: 50%;
    margin-bottom: env(safe-area-inset-bottom);
  }

  .download-container {
    margin-top: 20px;
    text-align: center;
  }

  .download-link {
    color: #8b5cf6;
    text-decoration: none;
    font-weight: bold;
  }

  .download-link:hover {
    text-decoration: underline;
  }
`;

export default StoryGameAiImageCropPopup;
